import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import SignInForm from '@/components/auth/SignInForm';
import SignUpForm from '@/components/auth/SignUpForm';
import PasswordResetForm from '@/components/auth/PasswordResetForm';
import { useAuth } from '@/hooks/useAuth';

type Mode = 'signin' | 'signup' | 'reset';

interface LocationState {
  from?: string;
  initialMode?: Mode;
}

export default function SignIn() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as LocationState | null;
  const [mode, setMode] = useState<Mode>(state?.initialMode ?? 'signin');

  useEffect(() => {
    if (state?.initialMode) setMode(state.initialMode);
  }, [state?.initialMode]);

  if (loading) return null;
  if (user) {
    const dest = state?.from && state.from !== '/signin' ? state.from : '/welcome';
    return <Navigate to={dest} replace />;
  }

  const titles: Record<Mode, { eyebrow: string; heading: string }> = {
    signin: { eyebrow: 'Sign In · Subscribers Only', heading: 'Welcome back.' },
    signup: { eyebrow: 'Create Account', heading: 'Join the catalog.' },
    reset: { eyebrow: 'Reset Password', heading: 'Send me a reset link.' },
  };
  const t = titles[mode];

  return (
    <div className="app">
      <div className="main">
        <div className="auth-shell">
          <div className="auth-shell-side">
            <div className="eyebrow">{t.eyebrow}</div>
            <h1 className="h-display" style={{ fontSize: 56 }}>{t.heading}</h1>
            <p className="lead" style={{ marginBottom: 24 }}>
              Subscribers see the full catalog of casting-verified, documented Hemi parts. Every
              tier ships with the SWC Promise — honest pricing, 100% guarantee, no-questions
              returns, free ship under 100 lbs.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn dark sm"
                onClick={() => navigate('/pricing')}
              >
                View Tiers →
              </button>
              <button type="button" className="btn ghost sm" onClick={() => navigate('/')}>
                ← Home
              </button>
            </div>
          </div>

          <div className="auth-shell-card card-elev">
            {mode === 'signin' && (
              <SignInForm
                onSuccess={() => navigate(state?.from && state.from !== '/signin' ? state.from : '/welcome', { replace: true })}
                onSwitchMode={(m) => setMode(m)}
              />
            )}
            {mode === 'signup' && (
              <SignUpForm
                onSuccess={() => navigate('/welcome', { replace: true })}
                onSwitchMode={(m) => setMode(m)}
              />
            )}
            {mode === 'reset' && <PasswordResetForm onSwitchMode={(m) => setMode(m)} />}
          </div>
        </div>
      </div>
    </div>
  );
}
